import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import BlogCard from '@/pages/blog/components/BlogCard';
import { useBlogPosts } from '@/hooks/useBlogPosts';
import { SITE_URL } from '@/components/base/Seo';

export default function BlogTag() {
  const { tag } = useParams<{ tag: string }>();
  const { publishedPosts } = useBlogPosts();

  const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
  const pathPrefix = basePath ? `/${basePath}` : '';

  const decodedTag = useMemo(() => {
    if (!tag) return '';
    try {
      return decodeURIComponent(tag);
    } catch {
      return tag;
    }
  }, [tag]);

  const matchingPosts = useMemo(() => {
    const target = decodedTag.trim().toLowerCase();
    if (!target) return [];
    return publishedPosts.filter((post) =>
      (post.tags || []).some((t) => t.trim().toLowerCase() === target),
    );
  }, [publishedPosts, decodedTag]);

  useEffect(() => {
    document.title = `#${decodedTag} Articles | Federica Tedeschi Blog`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        'content',
        `Browse every article tagged #${decodedTag} on the Healing & Breakthrough Blog by Federica Tedeschi, Energy-Safe Transformational Coach.`,
      );
    }

    const setProperty = (property: string, content: string) => {
      let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement | null;
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    setProperty('og:url', `${SITE_URL}/blog/tag/${encodeURIComponent(decodedTag)}`);
    setProperty('og:type', 'website');
    setProperty('og:title', `#${decodedTag} Articles | Federica Tedeschi Blog`);

    return () => {
      document.title = 'Federica Tedeschi | Energy-Safe Transformational Coach for Chronic Fatigue & Pain';
      if (metaDesc) {
        metaDesc.setAttribute(
          'content',
          'Energy-Safe Transformational Coach for Chronic Fatigue & Pain. CCE-ICF Accredited.',
        );
      }
      document
        .querySelectorAll('meta[property="og:url"], meta[property="og:type"], meta[property="og:title"]')
        .forEach((el) => el.remove());
    };
  }, [decodedTag]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Header */}
        <section className="relative w-full overflow-hidden">
          <div className="relative w-full h-[220px] md:h-[260px]">
            <img
              src="https://readdy.ai/api/search-image?query=A%20serene%20calm%20healing%20space%20with%20a%20wooden%20desk%20and%20an%20open%20journal%20next%20to%20a%20small%20potted%20plant%2C%20soft%20morning%20light%20streaming%20through%20sheer%20curtains%2C%20warm%20cream%20and%20sage%20green%20aesthetic%2C%20peaceful%20minimalist%20home%20office%2C%20botanical%20elements%2C%20editorial%20lifestyle%20photography%20with%20soft%20shadows%20and%20dreamy%20atmosphere&width=1600&height=800&seq=blog-hero-bg&orientation=landscape"
              alt="Blog tag header background"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/35" />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
              <a
                href={`${pathPrefix}/blog`}
                className="inline-flex items-center gap-1.5 text-xs text-white/80 hover:text-white transition-colors mb-3 cursor-pointer"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm" />
                </span>
                Back to Blog
              </a>
              <span className="text-xs font-medium px-3 py-1 rounded-full bg-white/15 text-white/90 backdrop-blur-sm mb-3 whitespace-nowrap">
                Topic
              </span>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                #{decodedTag}
              </h1>
              <p className="text-sm text-white/85 mt-2">
                {matchingPosts.length} {matchingPosts.length === 1 ? 'article' : 'articles'} on this topic
              </p>
            </div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="section-padding max-w-6xl mx-auto">
          {matchingPosts.length === 0 ? (
            <div className="text-center py-20">
              <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <i className="ri-price-tag-3-line text-4xl text-foreground-300" />
              </span>
              <h2 className="font-heading text-2xl font-semibold text-foreground-800 mb-2">
                No articles found
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                There are no published articles tagged #{decodedTag} just yet.
              </p>
              <a
                href={`${pathPrefix}/blog`}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-primary-500 text-background-50 rounded-md hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm" />
                </span>
                Browse all articles
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {matchingPosts.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}